// /app/frontend/src/components/AuthCallback.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const AuthCallback = ({ onComplete }) => {
  const { verifyMagicLink } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleVerify = async (token) => {
      const result = await verifyMagicLink(token);
      
      if (result.success) {
        setStatus('success');
        // Clean up URL and redirect after brief delay
        window.history.replaceState({}, '', '/');
        setTimeout(() => {
          onComplete?.();
        }, 1500);
      } else {
        setStatus('error');
        setErrorMessage(result.error || 'Failed to verify magic link');
      }
    };

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      handleVerify(token);
    } else {
      setStatus('error');
      setErrorMessage('No authentication token found');
    }
  }, [verifyMagicLink, onComplete]);

  const handleRetry = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
      <div className="text-center p-8 max-w-md">
        {status === 'verifying' && (
          <div className="space-y-4">
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-[#00284F]" />
            <h2 className="text-xl font-semibold">Verifying your sign in...</h2>
            <p className="text-muted-foreground">Please wait while we authenticate you.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold">Successfully signed in!</h2>
            <p className="text-muted-foreground">Redirecting to the application...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold">Authentication failed</h2>
            <p className="text-muted-foreground">{errorMessage}</p>
            <Button onClick={handleRetry} className="mt-4">
              Try again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
